import { Button, Empty, Modal, Table, TableProps, Select } from 'antd';
import { useEffect, useState } from 'react';

// services
import NotificationService from 'core/services/notification';
import { onGetShareTransactions } from 'modules/users/services/users';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { SearchOutlined } from '@ant-design/icons';

// models
import { ShareTransaction } from 'modules/users/models/share-transaction';
import ShareTransactionDetailsComponent from '../share-transactions-details/share-transactions-details';
import { getTopPosition } from 'utils/elementTopPosition';
import { useTranslation } from 'react-i18next';
import NoData from 'components/noData/noData';

interface InputProps {
	id: number;
}

interface Option {
	value: string;
	label: string;
}

// const customStyles: StylesConfig<Option, true> = {
// 	control: (provided, state) => ({
// 		...provided,
// 		backgroundColor: '#fff',
// 		borderColor: state.isFocused ? '#3e97ff' : '#ccc',
// 		boxShadow: 'none',
// 		'&:hover': { borderColor: '#3e97ff' },
// 		width: '400px',
// 		maxWidth: '100%',
// 	}),
// 	option: (provided, state) => ({
// 		...provided,
// 		backgroundColor: state.isSelected ? '#0073e6' : state.isFocused ? '#e6f7ff' : 'white',
// 		color: 'black',
// 		'&:active': { backgroundColor: '#3e97ff', color: 'white' },
// 	}),
// 	multiValue: (provided) => ({
// 		...provided,
// 		backgroundColor: '#e6f7ff',
// 		color: '#0073e6',
// 	}),
// 	multiValueLabel: (provided) => ({
// 		...provided,
// 		color: '#3e97ff',
// 	}),
// 	multiValueRemove: (provided) => ({
// 		...provided,
// 		color: '#3e97ff',
// 		':hover': {
// 			backgroundColor: '#3e97ff',
// 			color: 'white',
// 		},
// 	}),
// };

function ShareTransactionsComponent({ id }: InputProps) {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [shareTransactions, setShareTransactions] = useState<ShareTransaction[]>([]);

	const [selectedShareTransaction, setSelectedShareTransaction] = useState<ShareTransaction>();
	const [detailsOpen, setDetailsOpen] = useState(false);

	const [selectedOption, setSelectedOption] = useState<string>('all');

	const options: Option[] = [
		{ value: 'all', label: t('USERS.ALL') },
		{ value: 'Pending', label: t('USERS.PENDING') },
		{ value: 'Rejected', label: t('USERS.REJECTED') },
		{ value: 'Canceled', label: t('USERS.CANCELED') },
		{ value: 'Expired', label: t('USERS.EXPIRED') },
		{ value: 'Completed', label: t('USERS.COMPLETED') },
	];

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	useEffect(() => {
		getShareTransactions();
	}, []);

	const columns: TableProps<ShareTransaction>['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{id}</span>,
		},
		{
			title: t('USERS.TYPE'),
			dataIndex: 'transaction_type',
			key: 'transaction_type',
			render: (transaction_type) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{transaction_type}</span>,
		},
		{
			title: t('USERS.COMPANY_NAME'),
			dataIndex: 'company_name',
			key: 'company_name',
			render: (company_name) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{company_name}</span>,
		},
		{
			title: t('USERS.COMPANY_CODE'),
			dataIndex: 'company_code',
			key: 'company_code',
			render: (company_code) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{company_code}</span>,
		},
		{
			title: t('USERS.ROLE'),
			dataIndex: 'transaction_role',
			key: 'transaction_role',
			render: (transaction_role) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{transaction_role}</span>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{status}</span>,
		},
		{
			title: t('USERS.STATUS_KEY'),
			dataIndex: 'status_key',
			key: 'status_key',
			render: (status_key) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{status_key}</span>,
		},
		{
			title: t('USERS.DATE'),
			dataIndex: 'date_and_time',
			key: 'date_and_time',
			render: (date_and_time) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{date_and_time}</span>,
		},
		{
			title: t('USERS.QUANTITY'),
			dataIndex: 'quantity',
			key: 'quantity',
			render: (quantity) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{quantity}</span>,
		},
		{
			title: t('USERS.PRICE_SHARE'),
			dataIndex: 'price_per_share',
			key: 'price_per_share',
			render: (price_per_share) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{price_per_share}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, transaction) => (
				<div>
					<Button type="primary" style={{ padding: '5px 10px' }} onClick={() => onViewTransaction(transaction)}>
						{t('COMMON.VIEW')}
					</Button>
				</div>
			),
		},
	];

	const getShareTransactions = async () => {
		try {
			setIsLoading(true);

			const response = await onGetShareTransactions(id);
			setShareTransactions(response.data.data.share_transactions);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error('Ops!', 'An error occurred when fetching shares transactions');
		}
	};

	const fetchMoreData = async (page: number) => {
		setIsLoadMore(true);

		const response = await onGetShareTransactions(id, page);
		setShareTransactions(response.data.data.share_transactions);

		setTimeout(() => {
			window.scrollTo({ behavior: 'smooth', top: getTopPosition('share-transactions-table') - 65 });
		});

		setCurrentPage(page);
		setIsLoadMore(false);
	};

	const onViewTransaction = (transaction: ShareTransaction) => {
		setSelectedShareTransaction(transaction);
		setDetailsOpen(true);
	};

	const onSearchHandler = async () => {
		setIsLoading(true);

		const response = await onGetShareTransactions(id, 1, selectedOption == 'all' ? null : selectedOption);
		setShareTransactions(response.data.data.share_transactions);
		setTotalRecords(response.data.data.pagination.total);

		setIsLoading(false);
	};

	return (
		<>
			<div className="mb-3 d-flex align-items-center justify-content-end gap-1">
				<Select
					style={{ minWidth: '250px' }}
					size="large"
					options={options}
					loading={isLoading}
					onChange={(status) => setSelectedOption(status)}
					defaultValue="all"
				/>

				<Button type="primary" icon={<SearchOutlined />} loading={isLoading} onClick={onSearchHandler}>
					{t('COMMON.SEARCH')}
				</Button>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div style={{ position: 'relative' }}>
						<Table
							id="share-transactions-table"
							rowKey="id"
							columns={columns}
							dataSource={shareTransactions}
							scroll={{ x: 400 }}
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}

						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>

					<Modal open={detailsOpen} footer={null} onCancel={() => setDetailsOpen(false)} width={1000}>
						{selectedShareTransaction && <ShareTransactionDetailsComponent transactionId={selectedShareTransaction.id} userId={id} />}
					</Modal>
				</>
			)}
		</>
	);
}

export default ShareTransactionsComponent;
